import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MapPin, Search } from "lucide-react";

/**
 * Modal for the "Explore a location" flow — types a place name, Nominatim geocode dropdown,
 * clicking a result calls onPick({ lat, lng, display_name }) so Home can drop a scout pin
 * and fly the map there.
 */
export default function ExploreLocationDialog({ open, onOpenChange, onPick }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        if (!open) { setQ(""); setResults([]); setUnavailable(false); }
    }, [open]);

    useEffect(() => {
        clearTimeout(timer.current);
        if (!q || q.length < 3) { setResults([]); setUnavailable(false); return; }
        timer.current = setTimeout(async () => {
            setBusy(true);
            setUnavailable(false);
            try {
                const { data } = await api.get(`/geocode?q=${encodeURIComponent(q)}`);
                setResults(data || []);
            } catch (err) {
                setResults([]);
                if (err?.response?.status === 503) setUnavailable(true);
            } finally { setBusy(false); }
        }, 400);
        return () => clearTimeout(timer.current);
    }, [q]);

    const pick = (s) => {
        onPick({ lat: s.lat, lng: s.lng, display_name: s.display_name });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="explore-location-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <MapPin size={18} className="text-primary" /> Explore a location
                    </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground -mt-1">
                    Type a city, street or postcode. Pick a result to drop an explore pin there.
                </p>
                <div className="glass rounded-full shadow-sm px-2 py-1.5 flex items-center gap-2 border border-stone-200">
                    <Search size={16} className="text-muted-foreground ml-2" />
                    <Input
                        data-testid="explore-location-input"
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="e.g. Brighton, EC1A 1BB, Portland Maine"
                        className="border-0 focus-visible:ring-0 shadow-none bg-transparent"
                    />
                </div>
                <div className="min-h-[60px]">
                    {busy && <div className="px-2 py-3 text-xs text-muted-foreground">Searching…</div>}
                    {!busy && q.length >= 3 && results.length === 0 && !unavailable && (
                        <div className="px-2 py-3 text-sm text-muted-foreground">No matches — try a city, street or postcode.</div>
                    )}
                    {unavailable && !busy && (
                        <div data-testid="explore-geo-unavailable" className="px-2 py-3 text-sm text-destructive">
                            Search is busy right now — try again in a moment.
                        </div>
                    )}
                    {results.length > 0 && (
                        <ul className="max-h-72 overflow-y-auto rounded-xl border border-stone-100 divide-y divide-stone-50">
                            {results.map((s, i) => (
                                <li key={`${s.lat}-${s.lng}-${i}`}>
                                    <button
                                        type="button"
                                        data-testid={`explore-pick-${i}`}
                                        onClick={() => pick(s)}
                                        className="w-full text-left px-3 py-2.5 hover:bg-secondary flex items-start gap-2 text-sm"
                                    >
                                        <MapPin size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                                        <span className="truncate">{s.display_name}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
